<template>
  <div id="login-container" class="min-h-screen flex items-center justify-center p-4 bg-gray-100">
    <div id="login-card" class="bg-white border border-gray-600/20 rounded-2xl w-full
      max-w-lg p-8 bg-gray-50">
      <div id="logo-section" class="text-center mb-8">
        <div class="inline-flex items-center justify-center h-16 mb-4">
          <h3 class="bg-brand text-2xl text-center text-green-800">
            <i class="fa-solid fa-fingerprint text-green-800 font-bold"></i>
            ether<span class="font-bold">.id</span>
          </h3>
        </div>
        <p class="text-gray-300 text-sm">Login Into Your Account</p>
      </div>

      <!-- Session expired banner -->
      <div v-if="sessionExpired" class="mb-6 flex items-start space-x-3 bg-amber-50 border border-amber-200
        text-amber-800 rounded-md px-4 py-3 text-sm">
        <i class="fa-solid fa-clock-rotate-left mt-0.5 flex-shrink-0"></i>
        <span>Your session has expired. Please sign in again to continue.</span>
      </div>

      <form id="login-form" class="space-y-6" @submit.prevent="handleLogin()">
        <div id="email-field" class="space-y-2">
          <label for="email" class="block text-sm font-medium text-gray-600">Email Address</label>
          <div class="relative">
            <div class="absolute inset-y-0 left-0 pl-3 flex items-center justify-center pointer-events-none">
              <i class="fa-solid fa-envelope text-green-600 leading-none"></i>
            </div>
            <input type="email" id="email" name="email"
              class="block w-full pl-10 pr-3 py-3 bg-white/10 border border-gray-500/20 rounded-md
              border-gray-300
              text-gray-600 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent" placeholder="john@example.com"
              v-model="emailAddress" required />
          </div>
        </div>
        <div id="password-field" class="space-y-2">
          <label for="password" class="block text-sm font-medium text-gray-600">Password</label>
          <div class="relative">
            <div class="absolute inset-y-0 left-0 pl-3 flex items-center justify-center pointer-events-none">
              <i class="fa-solid fa-lock text-green-600 leading-none"></i>
            </div>
            <input type="password" id="password" name="password"
              class="block w-full pl-10 pr-12 py-3 bg-white/10 border border-gray-500/20 rounded-md
              text-gray-600 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent" placeholder="••••••••••"
              v-model="password" required />
            <div class="absolute inset-y-0 right-0 pr-3 flex items-center">
              <button type="button" class="text-gray-400 hover:text-gray-300" @click="togglePassword">
                <i id="password-toggle" class="fa-solid fa-eye"></i>
              </button>
            </div>
          </div>
        </div>
        <div id="remember-section" class="flex items-center justify-between">
          <div class="flex items-center">
            <input id="remember-me" name="remember-me" type="checkbox"
              class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded bg-white/10 border-white/20" />
            <label for="remember-me" class="ml-2 block text-sm text-gray-300">
              Remember me
            </label>
          </div>
          <div class="text-sm">
            <span class="text-blue-400 hover:text-blue-300 transition-colors cursor-pointer">
              Forgot password?
            </span>
          </div>
        </div>
        <button type="submit" id="login-button" :disabled="loading"
          class="w-full flex justify-center items-center py-3 px-4 border border-transparent
          rounded-sm shadow-sm text-sm font-medium text-white bg-gradient-to-r from-green-700
          to-green-800 hover:from-green-700 hover:to-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200 transform hover:scale-[1.02] disabled:opacity-70 disabled:cursor-not-allowed disabled:transform-none">
          <i v-if="!loading" class="fa-solid fa-sign-in-alt mr-2"></i>
          <i v-else class="fa-solid fa-circle-notch fa-spin mr-2"></i>
          {{ loading ? 'Signing in...' : 'Sign In to Dashboard' }}
        </button>
      </form>
      <div id="security-info" class="mt-8 pt-6 border-t border-white/20">
        <div class="flex items-center justify-center space-x-4 text-sm text-gray-400">
          <div class="flex items-center">
            <i class="fa-solid fa-shield-check mr-2 text-green-400"></i>
            <span>Secure Login</span>
          </div>
          <div class="flex items-center">
            <i class="fa-solid fa-key mr-2 text-blue-400"></i>
            <span>2FA Protected</span>
          </div>
        </div>
      </div>
      <div id="footer-links" class="mt-6 text-center text-xs text-gray-500">
        <p>© 2025 EtherOne Africa. All rights reserved.</p>
        <div class="mt-2 space-x-4">
          <span class="hover:text-gray-400 transition-colors cursor-pointer">Privacy Policy</span>
          <span>•</span>
          <span class="hover:text-gray-400 transition-colors cursor-pointer">Terms of Service</span>
          <span>•</span>
          <span class="hover:text-gray-400 transition-colors cursor-pointer">Support</span>
        </div>
        <img src="/assets/images/logo/logo.png" width="100" class="w-60 mx-auto p-4" alt="EtherID Logo" />
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import { useAuthStore } from '@/store/auth';
import { useNotificationStore } from '@/store/notifications';
import { ref, computed } from 'vue';
import { useRouter, useRoute } from 'vue-router';

const emailAddress = ref('');
const password = ref('');
const loading = ref(false);
const error = ref('');
const success = ref(false);
const router = useRouter();
const route = useRoute();
const authStore = useAuthStore();
const notificationStore = useNotificationStore();

const sessionExpired = computed(() => route.query.expired === 'true');

async function handleLogin() {
  error.value = '';
  success.value = false;

  if (!emailAddress.value || !password.value) {
    notificationStore.showError('Validation Error', 'Please enter both email address and password.');
    return;
  }

  loading.value = true;
  try {
    await authStore.login(emailAddress.value, password.value);

    if (authStore.error) {
      notificationStore.showError('Login Failed', authStore.error);
    } else {
      notificationStore.showSuccess('Login Successful', 'Welcome back! Redirecting...');
      const redirectPath = sessionStorage.getItem('redirectPath') || '/dashboard';
      sessionStorage.removeItem('redirectPath');
      setTimeout(() => {
        router.push(redirectPath);
      }, 1500);
    }
  } catch (err) {
    notificationStore.showError('Login Error', 'An unexpected error occurred during login.');
  } finally {
    loading.value = false;
  }
}

function togglePassword() {
  const passwordInput = document.getElementById('password') as HTMLInputElement;
  const toggleIcon = document.getElementById('password-toggle');

  if (passwordInput && toggleIcon) {
    if (passwordInput.type === 'password') {
      passwordInput.type = 'text';
      toggleIcon.classList.remove('fa-eye');
      toggleIcon.classList.add('fa-eye-slash');
    } else {
      passwordInput.type = 'password';
      toggleIcon.classList.remove('fa-eye-slash');
      toggleIcon.classList.add('fa-eye');
    }
  }
}
</script>
